import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import {
  createUser,
  createUserFromEmployee,
  deleteUser,
  fetchUsers,
  resetUserPassword,
  updateUser,
  type CreateUserFromEmployeeBody,
  type UserWriteBody,
} from './api'
import { ROLES_KEY } from '@/features/roles/hooks'
import { EMPLOYEES_KEY } from '@/features/employees/hooks'

export const USERS_KEYS = {
  users: ['users'] as const,
}

// Outbox keys (see apps/frontend/AGENTS.md "offline outbox").
export const USER_CREATE_KEY = ['outbox', 'users', 'create'] as const
export const USER_UPDATE_KEY = ['outbox', 'users', 'update'] as const
export const USER_DELETE_KEY = ['outbox', 'users', 'delete'] as const
export const USER_RESET_PASSWORD_KEY = ['outbox', 'users', 'reset-password'] as const

queryClient.setMutationDefaults(USER_CREATE_KEY, {
  mutationFn: (body: UserWriteBody & { username: string; password: string }) => createUser(body),
})
queryClient.setMutationDefaults(USER_UPDATE_KEY, {
  mutationFn: ({ id, body }: { id: string; body: UserWriteBody }) => updateUser(id, body),
})
queryClient.setMutationDefaults(USER_DELETE_KEY, { mutationFn: deleteUser })
queryClient.setMutationDefaults(USER_RESET_PASSWORD_KEY, { mutationFn: resetUserPassword })

function useInvalidateUsers() {
  const qc = useQueryClient()
  // Role user counts change whenever users are created or deleted.
  return () => {
    qc.invalidateQueries({ queryKey: USERS_KEYS.users })
    qc.invalidateQueries({ queryKey: ROLES_KEY })
  }
}

export function useUsers() {
  return useQuery({
    queryKey: USERS_KEYS.users,
    queryFn: fetchUsers,
  })
}

export function useCreateUser() {
  const invalidate = useInvalidateUsers()
  return useMutation({
    mutationKey: USER_CREATE_KEY,
    mutationFn: (body: UserWriteBody & { username: string; password: string }) => createUser(body),
    onSuccess: invalidate,
  })
}

export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: USER_UPDATE_KEY,
    mutationFn: ({ id, body }: { id: string; body: UserWriteBody }) => updateUser(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEYS.users }),
  })
}

export function useDeleteUser() {
  const invalidate = useInvalidateUsers()
  return useMutation({
    mutationKey: USER_DELETE_KEY,
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: invalidate,
  })
}

export function useResetUserPassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: USER_RESET_PASSWORD_KEY,
    mutationFn: (id: string) => resetUserPassword(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEYS.users }),
  })
}

// Create a login from an employee (Employees tab). Not an outbox mutation —
// the one-time password it returns must reach the admin, so it fails fast
// offline.
export function useCreateEmployeeLogin() {
  const qc = useQueryClient()
  return useMutation({
    networkMode: 'always',
    mutationFn: (body: CreateUserFromEmployeeBody) => createUserFromEmployee(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEYS.users })
      qc.invalidateQueries({ queryKey: ROLES_KEY })
      qc.invalidateQueries({ queryKey: EMPLOYEES_KEY })
    },
  })
}
